import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as s from './styled-notifications';
import {
  matchesNotificationFilter,
  getNotificationClient,
  getNotificationMachine,
  getNotificationVariable,
  getNotificationDate,
  getNotificationAlarmType,
} from '../../utils/notification-alerts';
import {
  getErrorAlertNotifications,
  patchErrorAlertVisualized,
} from '../../services';
import { EmptyState } from '../../components';
import { notifyError, notifySuccess, useDebounce } from '../../utils';

// Filtros clicaveis no header — alternam estados. 'todos' = sem filtro.
// $variant define cor accent quando ativo (mapeado no FilterChip).
const STATUS_FILTERS = [
  { key: 'todos', label: 'Todos', variant: 'blue' },
  { key: 'pendente', label: 'Pendentes', variant: 'red' },
  { key: 'visualizado', label: 'Visualizados', variant: 'green' },
];
const TYPE_FILTERS = [
  { key: 'alarme', label: 'Alarme', variant: 'red' },
  { key: 'atencao', label: 'Atenção', variant: 'orange' },
];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// SVG inline (lucide-style) usados na tela.
const IBackArrow = (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);
const ICheckMark = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m4 12 5 5 11-12" />
  </svg>
);
const IEye = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const getNotificationTimestamp = ({ timestamp, lastUpdate }) => {
  if (Number.isFinite(Number(timestamp))) return Number(timestamp);

  if (lastUpdate) {
    const parsed = new Date(lastUpdate).getTime();
    if (Number.isFinite(parsed)) return parsed / 1000;
  }

  return 0;
};

const sortUnreadFirst = (items) =>
  [...items].sort((first, second) => {
    if (first.visualized !== second.visualized) {
      return first.visualized ? 1 : -1;
    }

    return getNotificationTimestamp(second) - getNotificationTimestamp(first);
  });

const Notificacoes = () => {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.usuario);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [typeFilter, setTypeFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentData, setCurrentData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [bulkMarking, setBulkMarking] = useState(false);
  const [pageSizeMenuOpen, setPageSizeMenuOpen] = useState(false);

  // Debounce do filtro de texto: o input continua controlled (responde
  // imediatamente ao usuario), mas o filteredData/counters so recomputam
  // 250ms apos a ultima tecla. Evita 5 passadas no array por keystroke.
  const debouncedFilterText = useDebounce(filterText, 250);

  // Token de fetch pra evitar race quando o operador muda itemsPerPage
  // ou currentPage rapidamente. getErrorAlertNotifications nao aceita
  // signal, entao usamos contador via ref — respostas obsoletas ignoradas.
  const fetchTokenRef = useRef(0);

  useEffect(() => {
    const token = ++fetchTokenRef.current;
    const pageSize = 100;
    setIsLoading(true);

    const normalizeItems = (data) =>
      Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

    getErrorAlertNotifications(pageSize, 1)
      .then(async ({ data }) => {
        if (token !== fetchTokenRef.current) return;
        const firstPageItems = normalizeItems(data);
        const apiTotalPages = Number(data?.totalPages ?? 1);

        if (apiTotalPages <= 1) {
          setCurrentData(firstPageItems);
          return;
        }

        const remainingResponses = await Promise.all(
          Array.from({ length: apiTotalPages - 1 }, (_, index) =>
            getErrorAlertNotifications(pageSize, index + 2),
          ),
        );

        if (token !== fetchTokenRef.current) return;

        const remainingItems = remainingResponses.flatMap((response) =>
          normalizeItems(response?.data),
        );
        setCurrentData([...firstPageItems, ...remainingItems]);
      })
      .catch(() => {
        if (token !== fetchTokenRef.current) return;
        setCurrentData([]);
      })
      .finally(() => {
        if (token === fetchTokenRef.current) setIsLoading(false);
      });
  }, []);

  // Filtragem combinada: status + tipo + texto da busca.
  const filteredData = useMemo(
    () =>
      sortUnreadFirst(currentData.filter((notification) => {
        if (statusFilter === 'pendente' && notification.visualized) return false;
        if (statusFilter === 'visualizado' && !notification.visualized)
          return false;
        if (typeFilter === 'alarme' && !notification.isAlarm) return false;
        if (typeFilter === 'atencao' && notification.isAlarm) return false;
        return matchesNotificationFilter(notification, debouncedFilterText);
      })),
    [currentData, statusFilter, typeFilter, debouncedFilterText],
  );

  const pageData = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  useEffect(() => {
    setTotalPages(
      filteredData.length ? Math.ceil(filteredData.length / itemsPerPage) : 0,
    );
    if (currentPage > 0 && currentPage >= Math.ceil(filteredData.length / itemsPerPage)) {
      setCurrentPage(0);
    }
  }, [filteredData.length, itemsPerPage, currentPage]);

  useEffect(() => {
    dispatch({
      type: 'NOTIFICATION/SET_NOTIFICATION_AMOUNT',
      amount: currentData.filter((notification) => !notification.visualized)
        .length,
    });
  }, [currentData, dispatch]);

  // Contadores pros badges dos filter chips
  const counters = useMemo(
    () => ({
      todos: currentData.length,
      pendente: currentData.filter((n) => !n.visualized).length,
      visualizado: currentData.filter((n) => n.visualized).length,
      alarme: currentData.filter((n) => n.isAlarm).length,
      atencao: currentData.filter((n) => !n.isAlarm).length,
    }),
    [currentData],
  );

  // Marca individual via PATCH + atualiza state otimista
  const handleVisualize = async (eventId) => {
    if (!eventId) return;
    try {
      await patchErrorAlertVisualized({ eventId });
      setCurrentData((arr) =>
        arr.map((n) =>
          n.eventId === eventId ? { ...n, visualized: true } : n,
        ),
      );
    } catch (e) {
      notifyError('Não foi possível marcar como visualizado');
    }
  };

  // Abre o alarme: navega pra tela operacional da maquina (mesmo destino do
  // "VER AGORA" do painel de Eventos) e marca como visualizado no caminho.
  // E o que "da sentido" a notificacao — antes era uma lista morta.
  const handleOpenAlarm = (n) => {
    if (!n) return;
    if (!n.thing && !n.appliance) {
      notifyError('Notificação sem máquina vinculada para abrir.');
      return;
    }
    if (n.eventId && !n.visualized) handleVisualize(n.eventId);
    history.push('/dashboard/customer/operational', {
      thing: n.thing,
      aplicacao: n.appliance,
      alert: n.alarm,
      alertEventId: n.eventId,
    });
  };

  // Marca todas as pendentes da pagina atual. Promise.allSettled em
  // paralelo (backend nao tem bulk endpoint).
  const handleMarkAllVisualized = async () => {
    const pending = currentData.filter((n) => !n.visualized && n.eventId);
    if (pending.length === 0) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Marcar ${pending.length} notificação(ões) como visualizada(s)?`,
    );
    if (!confirmed) return;

    setBulkMarking(true);
    const results = await Promise.allSettled(
      pending.map((n) =>
        patchErrorAlertVisualized({ eventId: n.eventId }).then(() => n.eventId),
      ),
    );
    const succeededIds = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);
    const failedCount = results.length - succeededIds.length;

    setCurrentData((arr) =>
      arr.map((n) =>
        succeededIds.includes(n.eventId) ? { ...n, visualized: true } : n,
      ),
    );

    if (failedCount === 0) {
      notifySuccess(
        `${succeededIds.length} notificação(ões) marcada(s) como visualizada(s)`,
      );
    } else if (succeededIds.length > 0) {
      notifyError(
        `${succeededIds.length} marcada(s), ${failedCount} falhou(aram)`,
      );
    } else {
      notifyError('Não foi possível marcar as notificações');
    }
    setBulkMarking(false);
  };

  // Pagination com elipses (mesmo padrao dos outros CRUDs)
  const pagList = useMemo(() => {
    const list = [];
    const total = totalPages;
    const pg = currentPage + 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('…');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('…');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [totalPages, currentPage]);

  // Breadcrumb back-target: customer-user vai pra /dashboard/customer,
  // others vao pro /dashboard. SEGUNDO/RESET limpa o contexto.
  const goBack = () => {
    if (user && user.customer) {
      history.push('/dashboard/customer');
    } else {
      dispatch({ type: 'SEGUNDO/RESET' });
      history.push('/dashboard');
    }
  };

  return (
    <s.Container className="fade-in">
      {/* Breadcrumb (.crumbs) */}
      <s.BreadcrumbWrapper>
        <button
          type="button"
          onClick={goBack}
          title="Voltar"
          aria-label="Voltar"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid var(--cco-line)',
            color: 'var(--cco-ink-mute)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
        >
          {IBackArrow}
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {user && !user.customer && (
            <>
              <button
                type="button"
                onClick={goBack}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--cco-blue)',
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: 0,
                }}
              >
                Dashboard
              </button>
              <span style={{ color: 'var(--cco-ink-faint)' }}>›</span>
            </>
          )}
          <span
            style={{
              color: 'var(--cco-ink)',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '5px 12px',
              borderRadius: 8,
              border: '1px solid var(--cco-line)',
            }}
          >
            Notificações
          </span>
        </div>
      </s.BreadcrumbWrapper>

      {/* Toolbar (.toolbar) — filter-chips + sep + mark-all + search + exhibit */}
      <s.Toolbar>
        <s.FilterGroup>
          {STATUS_FILTERS.map(({ key, label, variant }) => (
            <s.FilterChip
              key={`status-${key}`}
              type="button"
              aria-pressed={statusFilter === key ? 'true' : 'false'}
              onClick={() => setStatusFilter(key)}
              $variant={variant}
            >
              {label}
              <s.FilterChipCount $darkText={variant !== 'red'}>
                {counters[key] || 0}
              </s.FilterChipCount>
            </s.FilterChip>
          ))}
        </s.FilterGroup>

        <s.SepV />

        <s.FilterGroup>
          {TYPE_FILTERS.map(({ key, label, variant }) => (
            <s.FilterChip
              key={`type-${key}`}
              type="button"
              aria-pressed={typeFilter === key ? 'true' : 'false'}
              onClick={() =>
                setTypeFilter((current) => (current === key ? null : key))
              }
              $variant={variant}
            >
              {label}
              <s.FilterChipCount $darkText={variant !== 'red'}>
                {counters[key] || 0}
              </s.FilterChipCount>
            </s.FilterChip>
          ))}
        </s.FilterGroup>

        {counters.pendente > 0 && (
          <s.MarkAll
            type="button"
            onClick={handleMarkAllVisualized}
            disabled={bulkMarking}
            title={`Marcar ${counters.pendente} pendente(s) como visualizada(s)`}
          >
            {bulkMarking ? <span>Marcando…</span> : <>{ICheckMark} Marcar todas</>}
          </s.MarkAll>
        )}

        <s.SearchInline>
          {ISearch}
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Pesquisar cliente, máquina, variável, data…"
          />
        </s.SearchInline>

        <s.Exhibit>
          Exibir
          <s.PageSizePicker>
            <s.PageSizeButton
              type="button"
              aria-haspopup="listbox"
              aria-expanded={pageSizeMenuOpen ? 'true' : 'false'}
              onClick={() => setPageSizeMenuOpen((open) => !open)}
            >
              {itemsPerPage}
            </s.PageSizeButton>
            {pageSizeMenuOpen && (
              <s.PageSizeMenu role="listbox">
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <s.PageSizeOption
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={itemsPerPage === option ? 'true' : 'false'}
                    onClick={() => {
                      setItemsPerPage(option);
                      setCurrentPage(0);
                      setPageSizeMenuOpen(false);
                    }}
                  >
                    {option}
                  </s.PageSizeOption>
                ))}
              </s.PageSizeMenu>
            )}
          </s.PageSizePicker>
          alertas
        </s.Exhibit>
      </s.Toolbar>

      {/* Table (.table) — grid com hairlines */}
      <s.TableWrap>
        <s.Table>
          <s.Th>Cliente</s.Th>
          <s.Th>Máquina</s.Th>
          <s.Th>Variável</s.Th>
          <s.Th>Data</s.Th>
          <s.Th>Status</s.Th>
          <s.Th $tipoCol>Tipo de alarme</s.Th>
          <s.Th $acoesCol>Ações</s.Th>

          {isLoading &&
            Array.from({ length: 6 }, (_, i) => `sk-notif-${i}`).map((key) => (
              <s.Tr key={key}>
                <s.SkeletonCell $variant="cliente" $w="70%" />
                <s.SkeletonCell $variant="maquina" $w="60%" />
                <s.SkeletonCell $variant="variavel" $w="65%" />
                <s.SkeletonCell $variant="data" $w="55%" />
                <s.SkeletonCell $w="45%" />
                <s.SkeletonCell $variant="tipo" $w="40%" />
                <s.SkeletonCell $variant="acoes" $w="40%" />
              </s.Tr>
            ))}

          {!isLoading &&
            pageData.map((n) => {
              const pillVariant = n.visualized
                ? 'viz'
                : n.isAlarm
                ? 'pend'
                : 'aten';
              const pillText = n.visualized
                ? 'VISUALIZADO'
                : n.isAlarm
                ? 'PENDENTE'
                : 'ATENÇÃO';
              return (
                <s.Tr
                  key={n.eventId || `${n.thingId}-${n.lastUpdate}`}
                  $viz={n.visualized}
                  $clickable={!!(n.thing || n.appliance)}
                  onClick={() => handleOpenAlarm(n)}
                  title="Abrir o alarme na tela operacional da máquina"
                >
                  <s.Tc $variant="cliente" data-label="Cliente">
                    <span className="bullet" />
                    <span>{getNotificationClient(n)}</span>
                  </s.Tc>
                  <s.Tc $variant="maquina" data-label="Máquina">{getNotificationMachine(n)}</s.Tc>
                  <s.Tc $variant="variavel" data-label="Variável">{getNotificationVariable(n)}</s.Tc>
                  <s.Tc $variant="data" data-label="Data">{getNotificationDate(n)}</s.Tc>
                  <s.Tc data-label="Status">
                    <s.StatusPill $variant={pillVariant}>
                      {pillText}
                    </s.StatusPill>
                  </s.Tc>
                  <s.Tc $variant="tipo" data-label="Tipo">
                    {getNotificationAlarmType(n) || (n.isAlarm ? 'Alarme' : 'Atenção')}
                  </s.Tc>
                  <s.Tc $variant="acoes" data-label="Ações">
                    <s.RowActions>
                      <s.ActBtn
                        className={`check ${n.visualized ? 'done' : ''}`}
                        type="button"
                        disabled={n.visualized}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisualize(n.eventId);
                        }}
                        title={
                          n.visualized
                            ? 'Já visualizado'
                            : 'Marcar como visualizado'
                        }
                        aria-label={
                          n.visualized
                            ? 'Notificação já visualizada'
                            : 'Marcar notificação como visualizada'
                        }
                      >
                        {ICheckMark}
                      </s.ActBtn>
                      <s.ActBtn
                        className="view"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAlarm(n);
                        }}
                        title="Abrir alarme na máquina"
                        aria-label="Abrir alarme na tela operacional da máquina"
                      >
                        {IEye}
                      </s.ActBtn>
                    </s.RowActions>
                  </s.Tc>
                </s.Tr>
              );
            })}

          {!isLoading && filteredData.length === 0 && (
            <s.EmptyRow>
              <EmptyState
                title="Nenhuma notificação encontrada"
                description="Ajuste a busca ou os filtros e tente novamente."
                actionLabel="Limpar filtros"
                onAction={() => {
                  setFilterText('');
                  setStatusFilter('todos');
                  setTypeFilter('todos');
                }}
              />
            </s.EmptyRow>
          )}
        </s.Table>
      </s.TableWrap>

      {/* Pager — Anterior / 1 2 3 ... N / Proximo */}
      {(currentPage > 0 || totalPages > 1) && (
        <s.Pager>
          <button
            className="btn"
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
            disabled={currentPage === 0 || isLoading}
          >
            Anterior
          </button>
          <div className="pages">
            {pagList.map((p, idx) =>
              p === '…' ? (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`dots-${idx}`} className="dots">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                  type="button"
                  onClick={() => setCurrentPage(p - 1)}
                >
                  {p}
                </button>
              ),
            )}
          </div>
          <button
            className="btn"
            type="button"
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage + 1 >= totalPages || isLoading}
          >
            Próximo
          </button>
        </s.Pager>
      )}
    </s.Container>
  );
};

export default Notificacoes;
